import React, { useState, useMemo, useEffect } from "react";
import { ChevronRight, TrendingUp, Loader2, Moon } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const PERIODS = [
  { id: "days", label: "Giorni" },
  { id: "weeks", label: "Settimane" },
  { id: "months", label: "Mesi" },
  { id: "all", label: "Tutti" },
];

// ---------- Quality Bar chart (teal) ----------
// Empty-state per grafici quando data è vuoto: mantiene asse e griglia,
// mostra messaggio centrato.
function ChartEmptyState({ height = 220 }) {
  return (
    <div
      className="rounded-[22px] overflow-hidden relative flex flex-col items-center justify-center text-white/75 text-[12px] text-center px-6"
      style={{
        height,
        background:
          "linear-gradient(160deg, #0fa88a 0%, #0b7d6a 55%, #074a42 100%)",
        boxShadow: "inset 0 1px 0 rgba(255,255,255,0.12)",
      }}
    >
      {/* Y axis dashed */}
      <div className="absolute left-12 top-4 bottom-10 w-[1px] bg-white/25" />
      {/* X axis */}
      <div className="absolute left-12 right-4 bottom-10 h-[1px] bg-white/30" />
      {/* Grid dashed */}
      {[0, 0.25, 0.5, 0.75, 1].map((p) => (
        <div
          key={p}
          className="absolute left-12 right-4 border-t border-dashed border-white/10"
          style={{ top: `calc(${20 + p * 80}% + 0px)` }}
        />
      ))}
      <div className="relative z-10 max-w-[260px] leading-snug">
        Nessun dato disponibile — connetti il tuo SmartDream per iniziare il
        monitoraggio
      </div>
    </div>
  );
}

function QualityBarChart({ data }) {
  if (!data || data.length === 0) return <ChartEmptyState />;
  const max = 100;
  const avg = Math.round(
    data.reduce((s, d) => s + d.value, 0) / data.length
  );
  const w = 400;
  const h = 220;
  const padT = 20;
  const padB = 44;
  const padL = 48;
  const padR = 10;
  const innerW = w - padL - padR;
  const innerH = h - padT - padB;
  const barW = innerW / data.length - 8;
  const ticks = [0, 25, 50, 75, 100];
  const yFor = (v) => padT + innerH - (v / max) * innerH;

  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-auto">
      <defs>
        <linearGradient id="qBar" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#4de3c4" />
          <stop offset="100%" stopColor="#0a8670" />
        </linearGradient>
        <filter id="qGlow" x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur stdDeviation="2.5" result="b" />
          <feMerge>
            <feMergeNode in="b" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      {/* grid */}
      {ticks.map((t, i) => {
        const y = yFor(t);
        return (
          <g key={i}>
            <line
              x1={padL}
              x2={w - padR}
              y1={y}
              y2={y}
              stroke="rgba(255,255,255,0.08)"
              strokeDasharray={t === avg ? "" : "2 4"}
            />
            <text
              x={padL - 10}
              y={y + 3}
              fontSize="13"
              textAnchor="end"
              fill="rgba(255,255,255,0.85)"
            >
              {t}%
            </text>
          </g>
        );
      })}

      {/* avg dashed line */}
      <line
        x1={padL}
        x2={w - padR}
        y1={yFor(avg)}
        y2={yFor(avg)}
        stroke="rgba(255,255,255,0.45)"
        strokeDasharray="5 5"
      />

      {/* bars */}
      {data.map((d, i) => {
        const x = padL + i * (innerW / data.length) + 4;
        const bh = (d.value / max) * innerH;
        const by = padT + innerH - bh;
        return (
          <g key={i}>
            <rect
              x={x}
              y={by}
              width={barW}
              height={Math.max(bh, 4)}
              rx={barW / 2}
              fill="url(#qBar)"
              filter="url(#qGlow)"
            />
            <text
              x={x + barW / 2}
              y={h - 22}
              fontSize="14"
              textAnchor="middle"
              fill="rgba(255,255,255,0.85)"
            >
              {d.day}
            </text>
            <text
              x={x + barW / 2}
              y={h - 8}
              fontSize="13"
              textAnchor="middle"
              fill="rgba(255,255,255,0.85)"
            >
              {d.date}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

// ---------- Regolarità range bars ----------
function RegularityChart({ data }) {
  // parse time "HH:MM" into minutes (allow wrap past midnight)
  const parse = (s) => {
    const [h, m] = s.split(":").map(Number);
    return h * 60 + m;
  };

  // Convert bedtime to minutes from a reference (use 20:00 as anchor, wrap)
  const anchor = 20 * 60; // 20:00
  const toAbs = (s) => {
    let v = parse(s) - anchor;
    if (v < 0) v += 24 * 60;
    return v;
  };

  const allMins = data.flatMap((d) => [toAbs(d.bed), toAbs(d.wake)]);
  const min = Math.min(...allMins);
  const max = Math.max(...allMins);
  const avgBed = Math.round(
    data.reduce((s, d) => s + toAbs(d.bed), 0) / data.length
  );
  const avgWake = Math.round(
    data.reduce((s, d) => s + toAbs(d.wake), 0) / data.length
  );

  const fmt = (m) => {
    const abs = (m + anchor) % (24 * 60);
    return `${String(Math.floor(abs / 60)).padStart(2, "0")}:${String(abs % 60).padStart(2, "0")}`;
  };

  const w = 400;
  const h = 240;
  const padT = 20;
  const padB = 50;
  const padL = 46;
  const padR = 10;
  const innerW = w - padL - padR;
  const innerH = h - padT - padB;
  const range = Math.max(max - min, 60);
  const yFor = (v) => padT + ((v - min) / range) * innerH;
  const barW = innerW / data.length - 8;

  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-auto">
      <defs>
        <linearGradient id="rBar" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#f8fafc" />
          <stop offset="100%" stopColor="#cbd5e1" />
        </linearGradient>
      </defs>
      {/* avg reference lines */}
      <line
        x1={padL}
        x2={w - padR}
        y1={yFor(avgBed)}
        y2={yFor(avgBed)}
        stroke="rgba(255,255,255,0.35)"
        strokeDasharray="4 4"
      />
      <line
        x1={padL}
        x2={w - padR}
        y1={yFor(avgWake)}
        y2={yFor(avgWake)}
        stroke="rgba(255,255,255,0.35)"
        strokeDasharray="4 4"
      />
      <text
        x={padL - 10}
        y={yFor(avgBed) + 3}
        fontSize="13"
        textAnchor="end"
        fill="rgba(255,255,255,0.95)"
      >
        {fmt(avgBed)}
      </text>
      <text
        x={padL - 10}
        y={yFor(avgWake) + 3}
        fontSize="13"
        textAnchor="end"
        fill="rgba(255,255,255,0.95)"
      >
        {fmt(avgWake)}
      </text>

      {data.map((d, i) => {
        const x = padL + i * (innerW / data.length) + 6;
        const top = yFor(toAbs(d.bed));
        const bottom = yFor(toAbs(d.wake));
        const bh = Math.max(bottom - top, 20);
        return (
          <g key={i}>
            <rect
              x={x}
              y={top}
              width={barW}
              height={bh}
              rx={barW / 2}
              fill="url(#rBar)"
            />
            <text
              x={x + barW / 2}
              y={h - 28}
              fontSize="14"
              textAnchor="middle"
              fill="#ffffff"
              fontWeight="600"
            >
              {d.quality}%
            </text>
            <text
              x={x + barW / 2}
              y={h - 14}
              fontSize="13"
              textAnchor="middle"
              fill="rgba(255,255,255,0.9)"
            >
              {d.day}
            </text>
            <text
              x={x + barW / 2}
              y={h - 2}
              fontSize="12"
              textAnchor="middle"
              fill="rgba(255,255,255,0.9)"
            >
              {d.date}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

// ---------- Time mini-bar (bedtime / wakeup) ----------
function TimeBarChart({ data, accent = "#14c2a1" }) {
  if (!data || data.length === 0) return <ChartEmptyState />;
  const toMin = (s) => {
    const [h, m] = s.split(":").map(Number);
    // Wrap: values after 20:00 map into that range, before 12:00 added +24h for continuity
    let total = h * 60 + m;
    if (h < 12) total += 24 * 60;
    return total;
  };
  const vals = data.map((d) => toMin(d.value));
  const min = Math.min(...vals) - 30;
  const max = Math.max(...vals) + 30;
  const avg = Math.round(vals.reduce((a, b) => a + b, 0) / vals.length);

  const w = 400;
  const h = 190;
  const padT = 14;
  const padB = 50;
  const padL = 46;
  const padR = 10;
  const innerW = w - padL - padR;
  const innerH = h - padT - padB;
  const barW = innerW / data.length - 8;
  const yFor = (v) => padT + ((v - min) / (max - min)) * innerH;

  const fmt = (total) => {
    const t = total % (24 * 60);
    return `${String(Math.floor(t / 60)).padStart(2, "0")}:${String(t % 60).padStart(2, "0")}`;
  };

  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-auto">
      <defs>
        <linearGradient id="tBar" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={accent} stopOpacity="0.9" />
          <stop offset="100%" stopColor={accent} stopOpacity="0.5" />
        </linearGradient>
      </defs>
      <line
        x1={padL}
        x2={w - padR}
        y1={yFor(avg)}
        y2={yFor(avg)}
        stroke="rgba(255,255,255,0.35)"
        strokeDasharray="4 4"
      />
      <text
        x={padL - 10}
        y={yFor(avg) + 3}
        fontSize="13"
        textAnchor="end"
        fill="rgba(255,255,255,0.95)"
      >
        {fmt(avg)}
      </text>
      {data.map((d, i) => {
        const x = padL + i * (innerW / data.length) + 6;
        const top = yFor(toMin(d.value));
        return (
          <g key={i}>
            <rect
              x={x}
              y={top}
              width={barW}
              height={Math.max(padT + innerH - top, 12)}
              rx={barW / 2}
              fill="url(#tBar)"
            />
            <text
              x={x + barW / 2}
              y={h - 28}
              fontSize="13"
              textAnchor="middle"
              fill="rgba(255,255,255,0.85)"
            >
              {d.value}
            </text>
            <text
              x={x + barW / 2}
              y={h - 14}
              fontSize="13"
              textAnchor="middle"
              fill="rgba(255,255,255,0.85)"
            >
              {d.day}
            </text>
            <text
              x={x + barW / 2}
              y={h - 2}
              fontSize="12"
              textAnchor="middle"
              fill="rgba(255,255,255,0.9)"
            >
              {d.date}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

import InfoPoint from "../components/InfoPoint";

function Section({ title, trend, children, infoKey }) {
  return (
    <section className="mt-7">
      <div className="flex items-center justify-between">
        <h2 className="text-white text-[28px] font-semibold tracking-tight flex items-center gap-2">
          {title}
          {infoKey && <InfoPoint keyName={infoKey} />}
        </h2>
        <button className="h-8 px-3 rounded-full bg-white/5 border border-white/10 text-white/85 text-[12px] font-medium flex items-center gap-1 hover:bg-white/10 transition-colors">
          Altro <ChevronRight size={12} />
        </button>
      </div>
      {trend && (
        <div className="mt-1 flex items-center gap-1.5 text-[#67e8d1] text-[12px]">
          <TrendingUp size={12} />
          {trend}
        </div>
      )}
      <div className="mt-3 rounded-2xl bg-white/[0.03] border border-white/5 p-3">
        {children}
      </div>
    </section>
  );
}

export default function Statistiche() {
  const [period, setPeriod] = useState("days");
  const { api } = useAuth();
  const [stats, setStats] = useState(null); // null=loading
  const [err, setErr] = useState("");

  const rangeFromPeriod = (p) => {
    if (p === "weeks") return "30d";
    if (p === "months") return "30d";
    if (p === "all") return "30d";
    return "7d";
  };

  useEffect(() => {
    let cancelled = false;
    setStats(null);
    setErr("");
    (async () => {
      try {
        const r = await api.get(`/api/user/me/stats?range=${rangeFromPeriod(period)}`);
        if (!cancelled) setStats(r.data);
      } catch (e) {
        if (!cancelled) setErr("Impossibile caricare le statistiche");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [api, period]);

  // Trasforma days[] (dal più recente) in arrays cronologici per i 4 chart.
  const data = useMemo(() => {
    if (!stats || !stats.days || stats.days.length === 0) return null;
    const ordered = [...stats.days].reverse();
    return {
      quality: ordered.map((d) => ({
        day: d.day_label,
        date: d.date.slice(5).replace("-", "/"),
        value: d.quality ?? 0, // null finché job non calcola → resta 0
      })),
      regularity: ordered.map((d) => ({
        day: d.day_label,
        date: d.date.slice(5).replace("-", "/"),
        bed: d.bedtime,
        wake: d.wakeup,
        quality: d.quality ?? 0,
      })),
      bedtime: ordered.map((d) => ({
        day: d.day_label,
        date: d.date.slice(5).replace("-", "/"),
        value: d.bedtime,
      })),
      wakeup: ordered.map((d) => ({
        day: d.day_label,
        date: d.date.slice(5).replace("-", "/"),
        value: d.wakeup,
      })),
    };
  }, [stats]);

  const isLoading = stats === null && !err;
  const hasNoData = !isLoading && !err && (!stats || stats.days_returned === 0);
  // Quando non ci sono dati, passiamo array vuoti alle sezioni (gestiscono empty state)
  const safeData = data || {
    quality: [],
    regularity: [],
    bedtime: [],
    wakeup: [],
  };
  const qualityAvg = data
    ? Math.round(data.quality.reduce((s, d) => s + (d.value || 0), 0) / Math.max(1, data.quality.length))
    : 0;

  return (
    <div className="px-4 pt-4 pb-36">
      <h1 className="text-white text-[42px] leading-none font-semibold tracking-tight">
        Statistiche
      </h1>

      {/* Period tabs */}
      <div className="mt-4 flex items-center gap-2 p-1 bg-white/5 border border-white/10 rounded-full">
        {PERIODS.map((p) => {
          const active = period === p.id;
          return (
            <button
              key={p.id}
              onClick={() => setPeriod(p.id)}
              className={`flex-1 h-9 rounded-full text-[13px] font-medium transition-all ${
                active
                  ? "bg-white text-[#0a2030]"
                  : "text-white/85 hover:bg-white/5"
              }`}
            >
              {p.label}
            </button>
          );
        })}
      </div>

      {/* Banner stato (non blocca le sezioni sotto) */}
      {isLoading && (
        <div
          className="mt-4 rounded-2xl border border-white/10 bg-white/5 px-4 py-2.5 flex items-center text-white/70 text-[12.5px] gap-2"
          data-testid="stats-loading"
        >
          <Loader2 size={14} className="animate-spin" />
          Caricamento statistiche…
        </div>
      )}

      {err && !isLoading && (
        <div
          className="mt-4 rounded-2xl border border-rose-400/30 bg-rose-500/10 px-4 py-2.5 text-rose-100 text-[12.5px]"
          data-testid="stats-error"
        >
          {err}
        </div>
      )}

      {hasNoData && (
        <div
          className="mt-4 rounded-2xl border border-[#14c2a1]/25 bg-[#14c2a1]/10 px-4 py-3 text-[12.5px] flex items-start gap-2"
          data-testid="stats-empty"
        >
          <Moon size={14} className="mt-0.5 text-[#67e8d1]" />
          <div>
            <div className="font-semibold text-white">
              Nessun dato disponibile
            </div>
            <div className="text-white/70 mt-0.5">
              Connetti il tuo SmartDream per iniziare il monitoraggio.
            </div>
          </div>
        </div>
      )}

      {/* Sezioni SEMPRE visibili (con empty state interno se vuote) */}
      <Section
        title="Qualità del sonno"
        trend={data ? `Media ${qualityAvg}%` : "—"}
        infoKey="score_mattress"
      >
        <QualityBarChart data={safeData.quality} />
      </Section>

      <Section title="Regolarità" infoKey="regularity">
        <RegularityChart data={safeData.regularity} />
      </Section>

      <Section title="Coricato" infoKey="sleep_latency">
        <TimeBarChart data={safeData.bedtime} accent="#14c2a1" />
      </Section>

      <Section title="Svegliato" infoKey="waso">
        <TimeBarChart data={safeData.wakeup} accent="#67e8d1" />
      </Section>
    </div>
  );
}
